const PLAYFUL_BAR_COLORS = [
  '#0ea5e9',
  '#14b8a6',
  '#f97316',
  '#8b5cf6',
  '#ef4444',
  '#eab308',
  '#ec4899',
  '#22c55e'
];

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getInteractiveBarColors = (count) => {
  const safeCount = Math.max(0, Number(count) || 0);
  return Array.from({ length: safeCount }, (_, index) => PLAYFUL_BAR_COLORS[index % PLAYFUL_BAR_COLORS.length]);
};

export const buildBarChartInsight = ({ labels = [], data = [], selectedIndex = null }) => {
  if (!Array.isArray(labels) || !Array.isArray(data) || labels.length === 0 || data.length === 0) {
    return null;
  }

  const normalized = labels.map((label, index) => ({
    index,
    label: String(label ?? '').trim() || '—',
    value: num(data[index])
  }));
  const total = normalized.reduce((sum, item) => sum + item.value, 0);
  const fallback = normalized.reduce((best, item) => (item.value > best.value ? item : best), normalized[0]);
  const active = normalized.find((item) => item.index === selectedIndex) || fallback;
  const sorted = [...normalized].sort((left, right) => right.value - left.value);
  const rank = sorted.findIndex((item) => item.index === active.index) + 1;
  const share = total > 0 ? (active.value / total) * 100 : 0;

  return {
    ...active,
    total,
    share,
    rank,
    itemCount: normalized.length,
    isPinned: selectedIndex != null && selectedIndex >= 0
  };
};

export const buildLineChartInsight = ({ months = [], series = [], selectedSeriesIndex = null, selectedPointIndex = null }) => {
  if (!Array.isArray(series) || series.length === 0 || !Array.isArray(months) || months.length === 0) {
    return null;
  }

  const normalizedSeries = series.map((entry, seriesIndex) => ({
    seriesIndex,
    name: String(entry?.name ?? '').trim() || `Series ${seriesIndex + 1}`,
    data: Array.isArray(entry?.data) ? entry.data.map(num) : []
  }));

  let activeSeries = normalizedSeries.find((entry) => entry.seriesIndex === selectedSeriesIndex) || null;
  let activePointIndex = Number.isInteger(selectedPointIndex) ? selectedPointIndex : null;

  if (!activeSeries || activePointIndex == null || activePointIndex < 0 || activePointIndex >= months.length) {
    let fallback = null;
    normalizedSeries.forEach((entry) => {
      entry.data.forEach((value, pointIndex) => {
        if (!fallback || value > fallback.value) {
          fallback = { seriesIndex: entry.seriesIndex, pointIndex, value };
        }
      });
    });

    if (!fallback) {
      return null;
    }

    activeSeries = normalizedSeries[fallback.seriesIndex];
    activePointIndex = fallback.pointIndex;
  }

  const value = num(activeSeries.data[activePointIndex]);
  const month = String(months[activePointIndex] ?? '').trim() || `Point ${activePointIndex + 1}`;
  const totalAtPoint = normalizedSeries.reduce((sum, entry) => sum + num(entry.data[activePointIndex]), 0);
  const shareAtPoint = totalAtPoint > 0 ? (value / totalAtPoint) * 100 : 0;

  let peakValue = value;
  let peakMonth = month;
  activeSeries.data.forEach((itemValue, pointIndex) => {
    if (itemValue > peakValue) {
      peakValue = itemValue;
      peakMonth = String(months[pointIndex] ?? '').trim() || peakMonth;
    }
  });

  return {
    seriesIndex: activeSeries.seriesIndex,
    dataPointIndex: activePointIndex,
    seriesName: activeSeries.name,
    month,
    value,
    totalAtPoint,
    shareAtPoint,
    peakValue,
    peakMonth,
    isPinned: selectedSeriesIndex != null && selectedPointIndex != null
  };
};